import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../layout/buttons';
import styles from './GameAssistantChat.module.css';
import { BoopAnimate, BoopTalk }from '../animations/boopAnimate';
import boopStatic from '../../assets/icons/augustsays/Boop-colour2.svg';
import { fetchCanvassStatus } from '../../utils/canvasses';
import { Container } from '../layout';
import { validateInterview, submitPreambleAnswers } from '../../utils/interviews';
import { logger } from '../../utils/logger';
import ReactMarkdown from 'react-markdown';
import { config } from '../../config/environment';

const GameAssistantChat = ({
  runtimeUrl,
  interviewId,
  onPills,
  onIntroduction,
  onInterviewId,
  onShouldTerminate,
  autoStart = true,
  disabled = false,
  disabledPlaceholder = 'Thank you so much for your time and insights. (Game)',
  introductionData,
}) => {
  const [messages, setMessages] = useState([]); // {role: 'assistant'|'user', content}
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [threadId, setThreadId] = useState('');
  const listRef = useRef(null);
  const [canStart, setCanStart] = useState(null);
  const [validationModal, setValidationModal] = useState({ open: false, summary: '', sessionId: null });
  const [isValidating, setIsValidating] = useState(false);
  const [isInitialStart, setIsInitialStart] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [preambleQuestions, setPreambleQuestions] = useState([]);
  const [currentPreambleIndex, setCurrentPreambleIndex] = useState(-1); // -1 means landing page, 0+ means question index
  const [preambleAnswers, setPreambleAnswers] = useState({});
  const [showPreambleQuestions, setShowPreambleQuestions] = useState(false);
  const [showContinueConfirmation, setShowContinueConfirmation] = useState(false);
  const [preambleCompleted, setPreambleCompleted] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [canvassData, setCanvassData] = useState(null);
  const [canvassName, setCanvassName] = useState('');
  const [canvassDescription, setCanvassDescription] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const dismissed = window?.localStorage?.getItem('augustsays_welcome_dismissed') === '1';
      setShowWelcomeModal(false);
    } catch {
      setShowWelcomeModal(false);
    }
  }, []);

  // Listen for end interview request from navigation bar
  useEffect(() => {
    const handleEndInterviewRequested = () => {
      setShowEndInterviewModal(true);
    };

    window.addEventListener('endInterviewRequested', handleEndInterviewRequested);
    
    return () => {
      window.removeEventListener('endInterviewRequested', handleEndInterviewRequested);
    };
  }, []);

  const dismissWelcomeModal = () => {
    try {
      window?.localStorage?.setItem('augustsays_welcome_dismissed', '1');
    } catch {}
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      try {
        if (!interviewId) { setCanStart(false); return; }
        const status = await fetchCanvassStatus({ canvassId: interviewId });
        const statusValue = (status?.status || '').toLowerCase();
        const introductionText = status?.introduction_text || null;

        if (introductionText) {
          const updatedIntroductionData = {
            ...introductionData,
            introduction_text: introductionText
          };
          onIntroduction(updatedIntroductionData);
        }

        if(status.name) {
          setCanvassName(status.name);
        }
        
        if(status.description) {
          setCanvassDescription(status.description);
        }
        
        // Store canvass data and check for preamble questions
        if (!aborted && status) {
          setCanvassData(status);
          const preambleQs = status.preamble_questions || [];
          setPreambleQuestions(preambleQs);
          
          // Show preamble questions only if they exist, haven't been shown yet, and preamble is not completed
          if (preambleQs.length > 0 && currentPreambleIndex === -1 && !preambleCompleted) {
            setShowPreambleQuestions(true);
            setCanStart(false); // Don't allow main interview to start yet
          } else {
            // If no preamble questions or preamble is completed, allow interview
            setCanStart(statusValue === 'approved' || statusValue === 'active');
          }
        }
      } catch {
        if (!aborted) setCanStart(false);
      }
    };
    run();
    return () => { aborted = true; };
  }, [interviewId, preambleCompleted]);

  // Debug: Track showPreambleQuestions changes
  useEffect(() => {
    console.log('[State Debug] showPreambleQuestions changed:', showPreambleQuestions);
    console.log('[State Debug] Current state:', {
      showPreambleQuestions,
      showContinueConfirmation,
      preambleAnswersCount: Object.keys(preambleAnswers).length,
      preambleCompleted,
      currentPreambleIndex
    });
  }, [showPreambleQuestions]);

  // Handle preamble completion - show continue prompt when ready
  useEffect(() => {
    const shouldShowContinuePrompt = !showPreambleQuestions && 
                                   !showContinueConfirmation && 
                                   Object.keys(preambleAnswers).length > 0 && 
                                   !preambleCompleted;
    
    console.log('[Preamble Completion] Should show continue prompt:', shouldShowContinuePrompt);
    console.log('[Preamble Completion] State:', {
      showPreambleQuestions,
      showContinueConfirmation,
      preambleAnswersCount: Object.keys(preambleAnswers).length,
      preambleCompleted,
      currentPreambleIndex
    });
    
    setShowContinuePrompt(shouldShowContinuePrompt);
  }, [showPreambleQuestions, showContinueConfirmation, preambleAnswers, preambleCompleted]);

  // Trigger interview start when canStart becomes true after continuing
  useEffect(() => {
    if (canStart && showContinueConfirmation && autoStart) {
      console.log('[Interview Start] canStart is true and confirmation showing, triggering onSend');
      setTimeout(() => {
        try {
          console.log('[Interview Start] Calling onSend now');
          onSend({ preventDefault: () => {} }, 'start');
          console.log('[Interview Start] onSend called successfully');
        } catch (error) {
          console.error('[Interview Start] Error calling onSend:', error);
        }
      }, 100);
    }
  }, [canStart, showContinueConfirmation, autoStart]);

  const handleValidationResponse = async (isUserValidated) => {
    if (!validationModal.sessionId) {
      setValidationModal({ open: false, summary: '', sessionId: null });
      return;
    }
    if (isValidating) return;
    setIsValidating(true);
    try {
      await validateInterview({
        sessionId: validationModal.sessionId,
        isUserValidated,
      });
    } catch (err) {
      logger.error('Failed to validate interview summary:', err);
    } finally {
      setIsValidating(false);
      setValidationModal({ open: false, summary: '', sessionId: null });
      // Navigate to different completion pages based on user validation
      if (isUserValidated) {
        navigate('/voice/complete');
      } else {
        const interviewUrl = window.location.href;
        navigate(`/voice/completed?interviewUrl=${encodeURIComponent(interviewUrl)}`);
      }
    }
  };

  const voiceUrl = useMemo(() => {
    // Use interview service directly instead of middleware
    // For local development, use localhost:7071, for production use configured endpoint
    // if (config.environment === 'development' || window.location.hostname === 'localhost') {
    //   return 'http://localhost:7071/api/chat';
    // }
    // For production/staging, append /chat to the base URL
    return `${config.interviewApiBase}/chat`;
  }, []);

  // Ensure a per-game thread id
  useEffect(() => {
    const generateUUID = () =>
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    try {
      const scope = `${window.location.pathname}`;
      const key = `game_thread:${scope}`;
      let existing = localStorage.getItem(key);
      
      // Only set preambleCompleted if there was an existing threadId from a previous session
      // (not one we just created in this session)
      const wasExistingBefore = existing !== null;
      
      if (!existing) {
        existing = generateUUID();
        localStorage.setItem(key, existing);
      }
      
      setThreadId(existing);
      
      // Set preambleCompleted only if this threadId existed before this page load
      // But only clear preamble answers if we don't already have active answers
      if (wasExistingBefore) {
        setPreambleCompleted(true);
        // Only clear preamble answers if we don't have any active answers
        if (Object.keys(preambleAnswers).length === 0) {
          setPreambleAnswers({}); // Clear any stale preamble answers
        }
      }
    } catch {
      setThreadId(generateUUID());
    }
  }, []); // Empty dependency array - run only once on mount

  // Optional auto-start message once, only after threadId is ready
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart) return;
    if (autoStartedRef.current) return;
    if (canStart !== true && !showPreambleQuestions) return; // wait until canvass is approved/active or preamble questions are shown
    if (!threadId) return; // wait until threadId is set by the init effect
    if (messages.length > 0) return;
    autoStartedRef.current = true;
    setIsInitialStart(true);
    // Fire and forget: directly invoke send with override text 'start'
    setTimeout(() => {
      try {
        onSend({ preventDefault: () => {} }, 'start');
      } catch {}
    }, 0);
  }, [autoStart, threadId, messages.length, canStart]);

  // Scroll to bottom
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isSending]);

  const appendMessage = (role, content) => {
    if (!content) return;
    setMessages(prev => [...prev, { role, content }]);
  };

  // When the latest message is from the assistant, mark it as the one being "spoken"
  useEffect(() => {
    if (!messages.length) return;
    const lastIndex = messages.length - 1;
    const last = messages[lastIndex];
    if (last.role === 'assistant') {
      setSpeakingIndex(lastIndex);
    }
  }, [messages]);

  const extractAssistantReply = (respJson) => {
    // Handle direct interview service response format
    const currentQuestion = respJson?.current_question;
    if (currentQuestion) {
      return currentQuestion;
    }
    
    // Fallback to old format for compatibility
    const g = respJson?.data?.generateCopilotResponse;
    if (g?.messages && Array.isArray(g.messages)) {
      for (let i = g.messages.length - 1; i >= 0; i--) {
        const m = g.messages[i];
        if (m?.role === 'assistant') {
          if (Array.isArray(m.content)) return m.content.join('\n');
          if (typeof m.content === 'string') return m.content;
        }
      }
    }
    return null;
  };

  const processExtensions = (data) => {
    try {
      // Handle direct interview service response format
      if (data.pills && onPills) onPills(data.pills);
      if (data.introduction && onIntroduction) onIntroduction(data.introduction);
      if (data.interview_id && onInterviewId) onInterviewId(data.interview_id);
      if (data.should_terminate && onShouldTerminate) onShouldTerminate(Boolean(data.should_terminate));

      // Fallback to old format for compatibility
      const ext = data?.data?.generateCopilotResponse?.extensions;
      if (ext) {
        if (onPills && 'pills' in ext) onPills(ext.pills);
        if (onIntroduction && 'introduction' in ext) onIntroduction(ext.introduction);
        if (onInterviewId && 'interview_id' in ext) onInterviewId(ext.interview_id);
        if (onShouldTerminate && 'shouldTerminate' in ext)
          onShouldTerminate(Boolean(ext.shouldTerminate));
      }

      const shouldTerminate = data.should_terminate || ext?.shouldTerminate;
      if (shouldTerminate) {
        try {
          const scope = `${window.location.pathname}`;
          const key = `game_thread:${scope}`;
          localStorage.removeItem(key);
        } catch {}
      }

      const userSummary = data.userUnderstandingSummary || ext?.userUnderstandingSummary;
      if (userSummary && shouldTerminate) {
        const sessionId = data.interview_id || ext?.interview_id || threadId || null;
        setValidationModal({
          open: true,
          summary: userSummary,
          summary: userSummary,
          sessionId,
        });
      }

      // Update the current question display (open vs predefined/mixed answer options)
      const isPredefinedLike = data.question_type === 'predefined' || data.question_type === 'mixed' || (data.answers && data.answers.length > 0);
      if (isPredefinedLike) {
        setCurrentQuestion(
          data.question
            ? { question_type: data.question_type || 'predefined', ...data.question }
            : {
                question_type: data.question_type || 'predefined',
                question_text: data.current_question,
                answers: data.answers
              }
        );
      } else {
        setCurrentQuestion(null);
      }
    } catch (err) {
      logger.error('Failed to process extensions:', err);
    }
  };

  const buildRequestBody = (userText, predefinedAnswer = null) => {
    // Convert messages to simple format for interview service
    const messagesToSend = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    })).filter(msg => msg.role && msg.content);
    
    // Add the new user message
    messagesToSend.push({
      role: 'user',
      content: userText
    });

    const body = {
      threadId: threadId,
      messages: messagesToSend,
      voiceId: interviewId, // Pass interviewId as voiceId for caching
      environment: config.environment,
      frontendUrl: config.frontendUrl
    };
    
    // Add preamble answers if we have them and this is the first message of the main interview
    if (Object.keys(preambleAnswers).length > 0 && !showPreambleQuestions && messages.length === 0) {
      body.preambleAnswers = preambleAnswers;
    }

    if (predefinedAnswer) {
      body.predefinedAnswer = predefinedAnswer;
    }
    
    return body;
  };

  const handlePredefinedAnswer = (answer) => {
    onSend(null, answer.label, answer);
  };

  const handlePreambleAnswer = (answerIndex) => {
    const currentQuestion = preambleQuestions[currentPreambleIndex];
    const answer = currentQuestion.answers[answerIndex];
    
    // Store the answer
    const updatedAnswers = {
      ...preambleAnswers,
      [currentQuestion.question_text || currentPreambleIndex]: {
        question: currentQuestion.question_text,
        answer: answer.label,
        anchor: answer.anchor,
        valence: answer.valence,
        answerIndex: answerIndex
      }
    };
    setPreambleAnswers(updatedAnswers);
    
    // Move to next question or finish
    if (currentPreambleIndex < preambleQuestions.length - 1) {
      setCurrentPreambleIndex(currentPreambleIndex + 1);
      console.log('[Preamble] Moving to next question:', currentPreambleIndex + 1);
    } else {
      // All preamble questions answered
      console.log('[Preamble] All questions answered, hiding preamble section');
      console.log('[Preamble] State before hiding:', {
        currentPreambleIndex,
        preambleQuestionsLength: preambleQuestions.length,
        showPreambleQuestions: true,
        showContinueConfirmation: false,
        preambleAnswersCount: Object.keys(preambleAnswers).length,
        preambleCompleted: false
      });
      
      // Move index beyond array bounds to ensure questions section is hidden
      setCurrentPreambleIndex(currentPreambleIndex + 1);
      setShowPreambleQuestions(false);
      
      console.log('[Preamble] State after completion:', {
        showPreambleQuestions: false,
        currentPreambleIndex: currentPreambleIndex + 1,
        preambleAnswersCount: Object.keys(preambleAnswers).length
      });
    }
  };

  const handleContinueToInterview = async () => {
    // Show confirmation briefly, then immediately show interview view with loading
    setShowContinueConfirmation(true);
    
    // Set canStart immediately to prevent "Sorry" message during transition
    const statusValue = (canvassData?.status || '').toLowerCase();
    setCanStart(statusValue === 'approved' || statusValue === 'active');
    setShowPreambleQuestions(false);
    setPreambleCompleted(true); // Mark preamble as completed
    
    // After a short delay, hide confirmation
    setTimeout(() => {
      setShowContinueConfirmation(false);
    }, 1000);
  };

  
  const onSend = async (e, overrideText, predefinedAnswer = null) => {
    console.log('[onSend] Called with overrideText:', overrideText);
    e?.preventDefault?.();
    
    console.log('[onSend] Checking conditions:', {
      disabled,
      canStart,
      canStartNotTrue: canStart !== true,
      text: overrideText !== undefined ? String(overrideText) : input,
      isSending
    });
    
    if (disabled || canStart !== true) {
      console.log('[onSend] Early return - disabled or canStart not true');
      return;
    }
    
    const text = (overrideText !== undefined ? String(overrideText) : input).trim();
    if (!text || isSending) {
      console.log('[onSend] Early return - no text or already sending');
      return;
    }
    
    console.log('[onSend] Proceeding with send');
    // stop any current speaking animation/static icon when a new send starts
    setSpeakingIndex(null);
    appendMessage('user', text);
    if (overrideText === undefined) setInput('');
    setIsSending(true);

    try {
      const body = buildRequestBody(text, predefinedAnswer);
      const headers = { 'Content-Type': 'application/json' };
      
      // Add functions key if available
      const functionsKey = process.env.REACT_APP_INTERVIEW_API_KEY;
      if (functionsKey) {
        headers['x-functions-key'] = functionsKey;
      }
      const res = await fetch(voiceUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setIsInitialStart(false);
      const assistant = extractAssistantReply(data);
      if (assistant) appendMessage('assistant', assistant);
      processExtensions(data);
    } catch (err) {
      logger.error('Interview chat send failed:', err);
      appendMessage('assistant', 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const onTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(e);
    }
  };

  const AnimatedText = ({ message, onComplete, isHtml = false }) => {
    const [wordIndex, setWordIndex] = useState(0);
    const tokens = useMemo(() => {
      const m = message || '';
      if (isHtml) {
        // For HTML content, we need to preserve HTML tags with their content while animating
        // This regex matches complete HTML elements with their content
        const htmlElementRegex = /<(strong|em|code|b|i|u|span|div|p|h[1-6]|ul|ol|li|blockquote|a)[^>]*>.*?<\/\1>|<[^>]*>|[^<]+/g;
        const matches = m.match(htmlElementRegex) || [];
        
        // Further split any remaining text content that's not part of HTML elements
        const tokens = [];
        matches.forEach(match => {
          if (match.startsWith('<') && match.includes('</')) {
            // This is a complete HTML element, keep it as one token
            tokens.push(match);
          } else if (match.startsWith('<')) {
            // This is an opening tag, keep it as one token
            tokens.push(match);
          } else if (match.endsWith('>')) {
            // This is a closing tag, keep it as one token
            tokens.push(match);
          } else {
            // This is plain text, split into words and spaces
            const textTokens = match.match(/(\S+|\s+)/g) || [];
            tokens.push(...textTokens);
          }
        });
        return tokens;
      } else {
        return m.match(/(\S+|\s+)/g) || [];
      }
    }, [message, isHtml]);
    const hasCompletedRef = useRef(false);

    useEffect(() => {
      if (!tokens.length) return;

      if (wordIndex >= tokens.length) {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (onComplete) onComplete();
        }
        return;
      }

      const timer = setInterval(() => {
        setWordIndex((prev) => (prev < tokens.length ? prev + 1 : prev));
      }, 60);
      return () => clearInterval(timer);
      // Intentionally *not* depending on onComplete so parent re-renders
      // (e.g. typing in textarea) do not restart the animation.
    }, [wordIndex, tokens.length]);

    const visibleCount = Math.min(wordIndex, tokens.length);

    return (
      <span className={styles.animatedTextWrap} style={{ whiteSpace: 'pre-wrap' }}>
        <span className={styles.animatedTextGhost} aria-hidden="true">
          {message || ''}
        </span>
        <span className={styles.animatedTextVisible}>
          {tokens.slice(0, visibleCount).map((t, i) => {
            if (isHtml && t.startsWith('<')) {
              // This is an HTML tag or element, render it directly
              return <span key={i} dangerouslySetInnerHTML={{ __html: t }} />;
            } else {
              // This is text content
              const isWhitespace = /^\s+$/.test(t);
              if (isWhitespace) return <span key={i}>{t}</span>;
              return (
                <span key={i} className={styles.animatedToken}>
                  {t}
                </span>
              );
            }
          })}
        </span>
      </span>
    );
  };

  const SpeakingMessage = ({ content }) => {
    const [isAnimating, setIsAnimating] = useState(true);

    const handleComplete = () => {
      if (!isAnimating) return;
      setIsAnimating(false);
    };

    const contentString = typeof content === 'string' ? content : String(content);
    const hasMarkdown = /\*\*.*?\*\*|\*.*?\*|`.*?`|^#{1,6}\s/m.test(contentString);

    // Process markdown to get the rendered content as a string
    const getProcessedContent = (text) => {
      // Simple markdown processing for basic formatting
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/^#{1,6}\s(.*)$/gm, '<strong>$1</strong>');
    };

    const processedContent = hasMarkdown ? getProcessedContent(contentString) : contentString;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          flexWrap: 'nowrap',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          {isAnimating ? (
            <BoopTalk />
          ) : (
            <img src={boopStatic} alt="Assistant" style={{ width: 55, height: 55}} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <AnimatedText
            message={processedContent}
            onComplete={handleComplete}
            isHtml={hasMarkdown}
          />
        </div>
      </div>
    );
  };

  const LoadingIndicator = ({ message }) => {
    const defaultTitle = 'Loading your interview…';
    const defaultBody =
      '\n\nThis is a private, anonymous conversation — built uniquely around your responses.' +
      '\nTake your time. Be honest. What you share helps create clarity and action.' +
      '\n\nLet’s start.';

    try {
      if (BoopAnimate && typeof BoopAnimate === 'function') {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'nowrap',
            }}
          >
            <div style={{ flex: '0 0 auto', width: 55, height: 55 }}>
              <BoopAnimate />
            </div>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              {message ? (
                message === '...' ? (
                  <div className={styles.thinkingDotsLarge} aria-label="Thinking" aria-live="polite">
                    <span></span><span></span><span></span>
                  </div>
                ) : (
                  <AnimatedText message={message} />
                )
              ) : (
                <>
                  <strong style={{ whiteSpace: 'pre-wrap' }}>{defaultTitle}</strong>
                  <AnimatedText message={defaultBody} />
                </>
              )}
            </div>
          </div>
        );
      }
    } catch {}
    return (
      <div className={styles.typingDots} aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
    );
  };

  const renderedMessages = useMemo(() => {
    let hiddenStart = false;
    return messages.map((m, idx) => {
      const isStart = typeof m.content === 'string' && m.content.trim().toLowerCase() === 'start';
      
      if (!hiddenStart && m.role === 'user' && isStart) {
        hiddenStart = true;
        return null; // hide first 'start' user message
      }
      const isAssistant = m.role === 'assistant';
      const isSpeaking = !isSending && isAssistant && idx === speakingIndex;
      return (
        <div key={idx} className={`${styles.message} ${m.role === 'user' ? styles.user : styles.assistant}`}>
          <div className={styles.bubble}>
            {isSpeaking ? <SpeakingMessage content={m.content} /> : 
             isAssistant ? (
               <ReactMarkdown 
                 components={{
                   // Customize styling for markdown elements
                   p: ({children}) => <p style={{ margin: '0 0', lineHeight: '1.4' }}>{children}</p>,
                   strong: ({children}) => <strong style={{ fontWeight: '600' }}>{children}</strong>,
                   em: ({children}) => <em style={{ fontStyle: 'italic'}}>{children}</em>,
                   ul: ({children}) => <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ul>,
                   ol: ({children}) => <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ol>,
                   li: ({children}) => <li style={{ margin: '0.25rem 0' }}>{children}</li>,
                   h1: ({children}) => <h1 style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0.5rem 0' }}>{children}</h1>,
                   h2: ({children}) => <h2 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0.5rem 0' }}>{children}</h2>,
                   h3: ({children}) => <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0.5rem 0' }}>{children}</h3>,
                   blockquote: ({children}) => (
                     <blockquote style={{
                       borderLeft: '3px solid #d1d5db',
                       paddingLeft: '0.75rem',
                       margin: '0.5rem 0',
                       fontStyle: 'italic',
                       color: '#6b7280'
                     }}>{children}</blockquote>
                   ),
                   code: ({children}) => (
                     <code style={{
                       backgroundColor: '#f3f4f6',
                       padding: '0.125rem 0.25rem',
                       borderRadius: '0.25rem',
                       fontSize: '0.875rem',
                       fontFamily: 'monospace'
                     }}>{children}</code>
                   )
                 }}
               >
                 {m.content}
               </ReactMarkdown>
             ) : (
               m.content
             )
            }
          </div>
        </div>
      );
    });
  }, [messages, isSending, speakingIndex]);

  const defaultBody = 
      '\n\nThis is a private, anonymous conversation — built uniquely around your responses.' +
      '\nTake your time. Be honest. What you share helps create clarity and action.' +
      '\n\nLet’s start.';

  const userReplyCount = useMemo(() => {
    return messages.filter((m) => {
      if (m.role !== 'user') return false;
      const isStart = typeof m.content === 'string' && m.content.trim().toLowerCase() === 'start';
      return !isStart;
    }).length;
  }, [messages]);

  // Communicate interview state to navigation bar
  useEffect(() => {
    const canEndInterview = userReplyCount >= 3 && !disabled && !isSending;
    window.dispatchEvent(new CustomEvent('interviewStateChanged', { 
      detail: { canEndInterview, disabled, isSending, userReplyCount }
    }));
  }, [userReplyCount, disabled, isSending]);

  if (canStart === false && !showPreambleQuestions && !showContinuePrompt) {
    return (
      <Container>
        <div className={styles.homepage}>
            <h1 className={styles.homepageTitle}>Sorry the interview is not yet available</h1>
        </div>
      </Container>
    );
  }

  return (
    <div className={styles.chatContainer}>
      {canStart === null && (
        <div style={{ padding: 16, color: '#6b7280' }}>Loading…</div>
      )}

      {validationModal.open && (
        <div className={styles.welcomeModalOverlay}>
          <div className={styles.validationModal}>
            <h2 className={styles.validationTitle}>Does this summary look correct?</h2>
            <p className={styles.validationSummary}>
              {validationModal.summary}
            </p>
            <div className={styles.validationButtons}>
              <Button
                type="button"
                variant="ghost"
                size="md"
                disabled={isValidating}
                onClick={() => handleValidationResponse(false)}
              >
                No, not quite
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={isValidating}
                onClick={() => handleValidationResponse(true)}
                className={styles.validationYesButton}
              >
                Yes, looks right
              </Button>
            </div>
          </div>
        </div>
      )}
      {preambleCompleted && !showPreambleQuestions && !showContinueConfirmation && !showContinuePrompt && (
        <>
        <div className={styles.preambleTitleBar}>
          <div className={styles.preambleTitleBarTitle}>
            {canvassName || 'Welcome'}
          </div>
          <div className={styles.preambleProgressDots}>
            {preambleQuestions.map((_, index) => (
              <div
                key={index}
                className={`${styles.preambleProgressDot} ${styles.completed}`}
              />
            ))}
          </div>
        </div>
        <div className={styles.preambleMainInterview}>
          <div ref={listRef} className={styles.messages}>
        {introductionData?.introduction_text && (
            <>
              <div className={styles.intro}>{introductionData.introduction_text}</div>
              <div className={styles.privacyReminder}>
                Privacy Reminder: Please don’t share personal information (PII) in your responses. Avoid names, emails, phone numbers, addresses, IDs, or anything that could identify you.
              </div>
            </>
        )}
        {renderedMessages}
        {isSending && (
          <>
              { isInitialStart && !introductionData?.introduction_text && ( 
                <>
                  <div className={styles.intro}>{defaultBody}</div>
                  <div className={styles.privacyReminder}>
                    Privacy Reminder: Please don’t share personal information (PII) in your responses. Avoid names, emails, phone numbers, addresses, IDs, or anything that could identify you.
                  </div>
                </>
              )}
              <div className={styles.bubble} aria-live="polite" aria-busy="true">
               <LoadingIndicator
                 message={
                    '...'
                 }
               />
             </div>
          </>
          // <div className={`${styles.message} ${styles.assistant}`}>
          //   <div className={styles.bubble} aria-live="polite" aria-busy="true">
          //     <LoadingIndicator
          //       message={
          //         isInitialStart
          //           ? undefined
          //           : '...'
          //       }
          //     />
          //   </div>
          // </div>
        )}
        </div>
        </div>
        </>
      )}

      {/* Preamble Landing Section */}
      {showPreambleQuestions && preambleQuestions.length > 0 && currentPreambleIndex === -1 && (
        <>
        <div className={styles.preambleTitleBar}>
          <div className={styles.preambleTitleBarTitle}>
            {canvassName || 'Welcome'}
          </div>
          <div className={styles.preambleProgressDots}>
            {preambleQuestions.map((_, index) => (
              <div
                key={index}
                className={`${styles.preambleProgressDot} ${index === 0 ? styles.active : ''}`}
              />
            ))}
          </div>
        </div>
        <div className={styles.preambleSectionLanding}>
          {/* <div className={styles.preambleLandingTitle}>
            {canvassName || 'Welcome'}
          </div> */}
          
          {introductionData?.introduction_text && (
            <div className={styles.preambleLandingIntro}>
              {introductionData.introduction_text}
            </div>
          )}
          
          {canvassDescription && (
            <div className={styles.preambleLandingDescription}>
              {canvassDescription}
            </div>
          )}
          
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={isSending}
            onClick={() => {
              // Move to first question
              setCurrentPreambleIndex(0);
            }}
            className={styles.preambleStartButton}
          >
            Start Questions
          </Button>
        </div>
        </>
      )}

      {/* Preamble Questions Section */}
      {showPreambleQuestions && preambleQuestions.length > 0 && currentPreambleIndex >= 0 && currentPreambleIndex < preambleQuestions.length && (
        <>
        <div className={styles.preambleTitleBar}>
          <div className={styles.preambleTitleBarTitle}>
            {canvassName || 'Welcome'}
          </div>
          <div className={styles.preambleProgressDots}>
            {preambleQuestions.map((_, index) => (
              <div
                key={index}
                className={`${styles.preambleProgressDot} ${
                  index === currentPreambleIndex ? styles.active : 
                  index < currentPreambleIndex ? styles.completed : ''
                }`}
              />
            ))}
          </div>
        </div>
        <div className={styles.preambleSection}>
          <div className={styles.preambleQuestionCounter}>
            Question {currentPreambleIndex + 1} of {preambleQuestions.length}
          </div>
          <div className={styles.preambleQuestion}>
            {preambleQuestions[currentPreambleIndex]?.question_text}
          </div>
          <div className={styles.preambleAnswersGrid}>
            {preambleQuestions[currentPreambleIndex]?.answers?.map((answer, index) => (
              <button
                key={index}
                onClick={() => handlePreambleAnswer(index)}
                disabled={isSending}
                className={styles.preambleAnswerButton}
              >
                <span className={styles.preambleAnswerLetter}>
                  {String.fromCharCode(65 + index)}.
                </span>
                {answer.label}
              </button>
            ))}
          </div>
        </div>
        </>
      )}

      {/* Continue Confirmation */}
      {showContinueConfirmation && (
        <>
        <div className={styles.preambleTitleBar}>
          <div className={styles.preambleTitleBarTitle}>
            {canvassName || 'Welcome'}
          </div>
          <div className={styles.preambleProgressDots}>
            {preambleQuestions.map((_, index) => (
              <div
                key={index}
                className={`${styles.preambleProgressDot} ${styles.completed}`}
              />
            ))}
          </div>
        </div>
        <div className={styles.preambleSectionConfirmation}>
          <div className={styles.preambleConfirmationTitle}>
            Perfect! Let's begin the main interview.
          </div>
          <div className={styles.preambleConfirmationSubtitle}>
            Starting your interview now...
          </div>
          <div className={styles.preamblePreparingBadge}>
            Preparing interview questions
          </div>
        </div>
        </>
      )}

      {/* Continue to Interview Prompt */}
      {showContinuePrompt && (
        <>
          <div className={styles.preambleTitleBar}>
            <div className={styles.preambleTitleBarTitle}>
              {canvassName || 'Welcome'}
            </div>
            <div className={styles.preambleProgressDots}>
              {preambleQuestions.map((_, index) => (
                <div
                  key={index}
                  className={`${styles.preambleProgressDot} ${styles.completed}`}
                />
              ))}
            </div>
          </div>
        <div className={styles.preambleSectionContinue}>
          <div className={styles.preambleContinueTitle}>
            Great! Thank you for answering those preliminary questions. Would you like to continue with the main interview?
          </div>
          <Button
            onClick={handleContinueToInterview}
            variant="primary"
            disabled={isSending}
            className={styles.preambleContinueYesButton}
          >
            Yes, Continue Interview
          </Button>
          <Button
            onClick={async () => {
              setPreambleCompleted(true);
              
              // Send preamble answers to backend for analysis and storage (fire and forget)
              try {
                const functionsKey = process.env.REACT_APP_INTERVIEW_API_KEY;
                submitPreambleAnswers({
                  functionsKey,
                  preambleData: {
                    interviewId: threadId,
                    canvassId: interviewId,
                    preambleQuestions: preambleQuestions,
                    preambleAnswers: preambleAnswers,
                    assistantId: canvassData?.assistantId
                  }
                }).then(result => {
                  console.log('Preamble submission successful:', result);
                }).catch(error => {
                  console.error('Error submitting preamble:', error);
                });
              } catch (error) {
                console.error('Error initiating preamble submission:', error);
              }
              
              // Treat interview as completed and navigate to completion page
              try {
                const scope = `${window.location.pathname}`;
                const key = `game_thread:${scope}`;
                localStorage.removeItem(key);
              } catch {}
              
              // Navigate to completion page (similar to validation flow)
              const interviewUrl = window.location.href;
              navigate(`/voice/complete?interviewUrl=${encodeURIComponent(interviewUrl)}`);
            }}
            variant="outline-neutral"
            disabled={isSending}
          >
            No, Stop Here
          </Button>
        </div>
        </>
      )}

      {preambleCompleted && !showPreambleQuestions && !showContinueConfirmation && !showContinuePrompt && (
        <div className={styles.composer}>
          {(currentQuestion?.question_type === 'predefined' || currentQuestion?.question_type === 'mixed') && (
            <div className={styles.preambleAnswersGrid}>
              {currentQuestion.answers.map((answer, answerIndex) => (
                <button
                  key={answerIndex}
                  className={styles.preambleAnswerButton}
                  onClick={() => handlePredefinedAnswer(answer)}
                  disabled={disabled || isSending}
                >
                  <span className={styles.preambleAnswerLetter}>
                    {String.fromCharCode(65 + answerIndex)}.
                  </span>
                  {answer.label}
                </button>
              ))}
            </div>
          )}
          {currentQuestion?.question_type !== 'predefined' && (
            <form className={styles.inputRow} onSubmit={onSend} disabled={disabled}>
              <textarea
                className={styles.textarea}
                placeholder={disabled ? disabledPlaceholder : 'Type your message...'}
                disabled={disabled}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onTextareaKeyDown}
                rows={2}
              />
              <Button
                type="submit"
                variant="ghost"
                size="md"
                iconOnly
                ariaLabel="Send"
                className={styles.sendBtn}
                disabled={disabled || isSending || !input.trim()}
                iconRight={{ name: 'arrows/arrow_upward' }}
              />
            </form>
          )}

          {userReplyCount >= 3 && (
            <div className={`${styles.endInterviewRow} ${styles.desktopOnly}`}>
              <div className={styles.endInterviewText}>
                What you say matters. If you feel heard, you can end the interview at any time.
              </div>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={disabled || isSending}
                onClick={() => setShowEndInterviewModal(true)}
              >
                End Interview
              </Button>
            </div>
          )}
        </div>
      )}

      {showWelcomeModal && (
        <div className={styles.welcomeModalOverlay} role="presentation">
          <div className={styles.welcomeModal} role="dialog" aria-modal="true" aria-label="Welcome">
            <div className={styles.welcomeModalContent}>
              <div className={styles.welcomeTitle}>Welcome</div>
              <div className={styles.welcomeBody}>
                <p>
                  You’ve been invited into a different kind of conversation. The team behind this experience is exploring a more human way to listen so what happens next reflects real experience, not assumptions.
                </p>
                <div className={styles.welcomeDivider}></div>
                <div className={styles.welcomeSectionTitle}>What to expect</div>
                <p>
                  This isn’t a survey. It’s a short, adaptive conversation that responds to what you share. You speak in your own words and the questions adjust as you go.
                </p>
                <p>
                  Most people finish in about 10 to 12 questions, but you decide when the conversation is complete. You can end it at any time using the button below the chat, or continue if you have more to say. You’re in control.
                </p>
                <div className={styles.welcomeDivider}></div>
                <div className={styles.welcomeSectionTitle}>One thing to know</div>
                <p>
                  Your responses are anonymous and combined with others to reveal patterns, not individuals.
                </p>
                {/* <div className={styles.welcomeDivider}>⸻</div> */}
              </div>
            </div>
            <div className={styles.welcomeModalFooter}>
              <Button onClick={dismissWelcomeModal}>
                Start the conversation
              </Button>
            </div>
          </div>
        </div>
      )}

      {showEndInterviewModal && (
        <div className={styles.welcomeModalOverlay} role="presentation">
          <div className={styles.welcomeModal} role="dialog" aria-modal="true" aria-label="End Interview Confirmation">
            <div className={styles.welcomeModalContent}>
              <div className={styles.welcomeTitle}>End Interview?</div>
              <div className={styles.welcomeBody}>
                <p>
                  Are you sure you want to end the interview? Your insights are valuable and help us understand real experiences better.
                </p>
                <p>
                  If you have more to share, continuing for just a few more questions can provide much richer insights. But you're in control - you can end whenever you feel heard.
                </p>
              </div>
            </div>
            <div className={styles.welcomeModalFooter}>
              <Button 
                variant="outline-neutral"
                onClick={() => setShowEndInterviewModal(false)}
              >
                Continue Sharing
              </Button>
              <Button 
                variant="primary"
                onClick={() => {
                  try {
                    onSend({ preventDefault: () => {} }, 'End the Interview');
                    setShowEndInterviewModal(false);
                  } catch {}
                }}
              >
                Yes, End Interview
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameAssistantChat;
